import React, { useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  ActionLink,
  CurrencyFormatters,
  TravelWalletSelectionWorkflow,
  getRestrictedATCText,
  getRestrictedUserTCTexts,
  filterOutSpendAndGetOffers,
  isSpendAndGetOffer,
  getCreditActivityGroups,
  formatPartialHistoryValidFromDate,
  getUpcomingCreditActivityItems,
} from "halifax";
import { CreditActivityDialog } from "@capone/portal";
import { CreditActivityDialog as VesperCreditActivityDialog } from "@vesper/portal";
import { CreditActivityDialog as CorpCreditActivityDialog } from "@capone-corporate/portal";
import { getConfigTenant, isCorpTenant, isVesperTenant } from "@capone/common";
import { Prices } from "@b2bportal/air-shopping-api";
import {
  AVAILABLE,
  C1_AUTO_SELECT_CARD_AT_CHECKOUT,
  getExperimentVariant,
  useExperiments,
} from "../../../context/experiments";
import {
  AccountPrice,
  CallState,
  CreditDetail,
  FiatPrice,
  RewardsAccount,
  Transaction,
  TravelWalletCredit,
  TravelWalletOffer,
} from "redmond";
import {
  APPLY_TRAVEL_CREDIT_TITLE,
  APPLY_TRAVEL_OFFER_TITLE,
  CREDIT_BALANCE_DESCRIPTION,
  CREDIT_HISTORY_BALANCE_TEXT,
  CREDIT_HISTORY_MODAL_LIST_HEADER,
  CREDIT_HISTORY_MODAL_SUBHEADER,
  getSelectedCreditDescription,
  getSelectedOfferDescription,
  TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT,
  TRAVEL_CREDIT_SUBTITLE,
  TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT,
  TRAVEL_OFFER_SUBTITLE,
  TRAVEL_OFFER_TOTAL_PAYMENT_TEXT,
  VIEW_CREDIT_HISTORY_CTA,
} from "./textConstants";
import { ClientContext } from "../../../App";
import {
  IResetPaymentCardSelectedAccounts,
  ISetCreditToApply,
  ISetOfferToApply,
  ISetSelectedRewardsPaymentTotal,
} from "../../book/actions/actions";
import { DO_NOT_APPLY_REWARDS_KEY } from "../../book/reducer";
import { RewardDisplayType } from "../type";

export interface ITravelWalletSelectionProps extends RouteComponentProps {
  className?: string;
  isMobile?: boolean;
  disabled?: boolean;
  isAnnualTravelCreditsExperiment?: boolean;
  isCreditAndOfferStackingExperimentV1?: boolean;
  isTravelSale?: boolean;
  offers: TravelWalletOffer[] | undefined;
  offerToApply: TravelWalletOffer | undefined;
  fetchApplicableTravelWalletItemsCallState: CallState;
  selectedPaymentMethodId: string;
  selectedRewardsPaymentAccountId: string | null | undefined;
  credits: TravelWalletCredit | undefined;
  creditBreakdown: CreditDetail[];
  creditsToApply: TravelWalletCredit | undefined;
  isStackedTravelWalletPaymentOnly: boolean;
  isTravelCreditPaymentOnly: boolean;
  isTravelWalletOfferPaymentOnly: boolean;
  creditsHistoryTransaction: Transaction[] | undefined;
  partialHistoryValidFromTime?: string;
  bestOverallOffer: TravelWalletOffer | undefined;
  rewardsAccounts: RewardsAccount[];
  maxApplicableTravelWalletCredit: number;
  setOfferToApply: (offer?: TravelWalletOffer | undefined) => ISetOfferToApply;
  resetPaymentCardSelectedAccounts: () => IResetPaymentCardSelectedAccounts;
  setSelectedRewardsPaymentTotal: (
    accountReferenceId: string,
    rewardsPaymentTotal: AccountPrice | null,
    rewardsPaymentInFiatCurrency: FiatPrice | null,
    total?: Prices | undefined,
  ) => ISetSelectedRewardsPaymentTotal;
  setCreditToApply: (
    credit?: TravelWalletCredit | undefined,
  ) => ISetCreditToApply;
  type?: RewardDisplayType;
}

const getCreditActivityDialogByTenant = () => {
  const tenant = getConfigTenant();
  if (isCorpTenant(tenant)) return CorpCreditActivityDialog;
  if (isVesperTenant(tenant)) return VesperCreditActivityDialog;
  return CreditActivityDialog;
};

export const TravelWalletSelection = (props: ITravelWalletSelectionProps) => {
  const {
    offers,
    offerToApply,
    setOfferToApply,
    credits,
    creditBreakdown,
    creditsToApply,
    setCreditToApply,
    disabled,
    isTravelCreditPaymentOnly,
    isTravelWalletOfferPaymentOnly,
    isStackedTravelWalletPaymentOnly,
    isMobile,
    creditsHistoryTransaction,
    selectedPaymentMethodId,
    selectedRewardsPaymentAccountId,
    setSelectedRewardsPaymentTotal,
    resetPaymentCardSelectedAccounts,
    bestOverallOffer,
    rewardsAccounts,
    isAnnualTravelCreditsExperiment,
    isCreditAndOfferStackingExperimentV1,
    maxApplicableTravelWalletCredit,
    isTravelSale,
    type = "flight",
    partialHistoryValidFromTime,
  } = props;

  const firstName =
    React.useContext(ClientContext).sessionInfo?.userInfo?.firstName || "";

  const filteredOffers = useMemo(
    () => (offers ? filterOutSpendAndGetOffers(offers) : undefined),
    [offers],
  );

  const filteredBestOverallOffer = useMemo(
    () =>
      bestOverallOffer && isSpendAndGetOffer(bestOverallOffer.descriptions)
        ? undefined
        : bestOverallOffer,
    [bestOverallOffer],
  );

  if (!filteredOffers) return null;

  const expState = useExperiments();
  const autoSelectExperimentOn =
    getExperimentVariant(
      expState.experiments,
      C1_AUTO_SELECT_CARD_AT_CHECKOUT,
    ) === AVAILABLE;

  useEffect(() => {
    if (credits) {
      setCreditToApply(credits);
    }
  }, [credits]);

  useEffect(() => {
    const hasRealRewardsSelection =
      selectedRewardsPaymentAccountId &&
      selectedRewardsPaymentAccountId !== DO_NOT_APPLY_REWARDS_KEY;

    if (selectedPaymentMethodId || hasRealRewardsSelection) {
      if (!autoSelectExperimentOn) {
        resetPaymentCardSelectedAccounts();
      }
      setSelectedRewardsPaymentTotal("", null, null);
    }
  }, [offerToApply, creditsToApply, autoSelectExperimentOn]);

  useEffect(() => {
    if (filteredBestOverallOffer) {
      setOfferToApply(filteredBestOverallOffer);
    }
  }, [filteredBestOverallOffer]);

  const ResolvedCreditActivityDialog = getCreditActivityDialogByTenant();

  return (
    <TravelWalletSelectionWorkflow
      isMobile={isMobile}
      offersAvailable={filteredOffers || []}
      creditsAvailable={credits}
      offerToApply={offerToApply}
      creditsToApply={creditsToApply}
      setOfferToApply={setOfferToApply}
      setCreditToApply={setCreditToApply}
      titles={{
        creditTitle: APPLY_TRAVEL_CREDIT_TITLE,
        creditSubtitle: TRAVEL_CREDIT_SUBTITLE,
        offersTitle: APPLY_TRAVEL_OFFER_TITLE,
        offersSubtitle: TRAVEL_OFFER_SUBTITLE,
      }}
      getCreditDescription={getSelectedCreditDescription}
      getOfferDescription={getSelectedOfferDescription}
      disabled={disabled}
      totalCoverageTexts={{
        creditsCoverTotalText: isTravelCreditPaymentOnly
          ? TRAVEL_CREDIT_TOTAL_PAYMENT_TEXT(type)
          : undefined,
        offerCoversTotalText: isTravelWalletOfferPaymentOnly
          ? TRAVEL_OFFER_TOTAL_PAYMENT_TEXT(type)
          : undefined,
        travelWalletCoverTotalText: isStackedTravelWalletPaymentOnly
          ? TRAVEL_CREDIT_AND_OFFER_TOTAL_PAYMENT_TEXT(type)
          : undefined,
      }}
      isTravelWalletCreditPaymentOnly={isTravelCreditPaymentOnly}
      isTravelWalletOfferPaymentOnly={isTravelWalletOfferPaymentOnly}
      maxApplicableCreditAmount={maxApplicableTravelWalletCredit}
      travelCreditHistoryProps={
        creditsHistoryTransaction?.length
          ? {
              modalContentProps: {
                header: CREDIT_BALANCE_DESCRIPTION(firstName),
                balanceText: CREDIT_HISTORY_BALANCE_TEXT(
                  CurrencyFormatters.getSymbol(credits?.amount.currency),
                  CurrencyFormatters.get(credits?.amount.currency).format(
                    (credits?.amount?.amount && credits?.amount?.amount * -1) ||
                      0,
                  ),
                ),
                subHeader: CREDIT_HISTORY_MODAL_SUBHEADER,
                creditListHeader: CREDIT_HISTORY_MODAL_LIST_HEADER,
              },
              viewHistoryCTAText: VIEW_CREDIT_HISTORY_CTA,
              creditTransactionsHistory: creditsHistoryTransaction,
              credit: credits,
              creditBreakdown,
              rewardsAccounts,
            }
          : undefined
      }
      creditActivityDialogSlot={
        creditsHistoryTransaction?.length ? (
          <ResolvedCreditActivityDialog
            trigger={<ActionLink content={VIEW_CREDIT_HISTORY_CTA} />}
            groups={getCreditActivityGroups(
              creditsHistoryTransaction,
              creditBreakdown,
            )}
            upcomingItems={getUpcomingCreditActivityItems(creditBreakdown)}
            partialHistoryValidFromDate={formatPartialHistoryValidFromDate(
              partialHistoryValidFromTime,
            )}
          />
        ) : undefined
      }
      isAnnualTravelCreditsExperiment={isAnnualTravelCreditsExperiment}
      isCreditAndOfferStackingExperimentV1={
        isCreditAndOfferStackingExperimentV1
      }
      restrictedATCText={
        getRestrictedATCText(rewardsAccounts, creditBreakdown) ||
        getRestrictedUserTCTexts(rewardsAccounts, creditBreakdown)?.[0]
      }
      isTravelSale={isTravelSale}
    />
  );
};
